/**
 * Category search view model (#2247, spec 6.3) - every non-rendering decision the filter bar's
 * category combobox makes, with no firebase, no API client and no runes, so a probe can drive the
 * whole thing with plain arrays.
 *
 * The rules, stated once:
 *
 *  - **Categories are the Sensitive Data Detection page's**: the keys of its
 *    `getCategoryMetaWithCustom` (built-in categories plus the org's enabled custom info types),
 *    plus one synthetic Uncategorised, listed last. "Custom Types" is listed before "Built-in
 *    Categories", alphabetical by label within each group, never by count.
 *  - **The saved vocabulary is still raw DLP names** (spec 425). A category's membership is every
 *    raw name the static `infoTypes` map puts under it plus the raw names in the rows that map to
 *    it, so a saved default older than today's membership reads as partial. A custom type is its
 *    own single member; Uncategorised has no static members, only the names on screen.
 *  - **Pruning is to the rows handed in**: the caller passes the rows of the current exposure +
 *    provider view, *not* trimmed to the active list, so counts never move with what is ticked. A
 *    ticked category whose count falls to zero stays listed at zero so it can be unticked.
 *
 * Custom info types arrive as `{ name, displayName }`, the shape `RollUpInfoTypesOptions` already
 * uses; the container fetches them and a failed fetch degrades to "no custom categories".
 */
import { categoryColour, getCategoryMetaWithCustom, type NamedCustomInfoType } from "../categoryLabels";
import { infoTypes } from "../infoTypes";
import {
  UNCATEGORISED_CATEGORY,
  UNCATEGORISED_LABEL,
  byLabelUncategorisedLast,
  categoryOf,
  customInfoTypesByName,
  detectorDisplayName,
  type ActiveInfoTypeSelection,
  type ExposedSensitiveDocument,
} from "../exposedSensitiveDocuments";
import { appliedInfoTypes } from "./activeInfoTypes";

export interface CategorySearchOptions {
  /** The org's *enabled* custom info types. Each is its own category (spec 477). */
  customInfoTypes?: NamedCustomInfoType[];
}

export interface CategoryListOptions extends CategorySearchOptions {
  /** The active raw names, or `null` for show-all. Decides only which zero-count rows stay. */
  active?: string[] | null;
}

/** One combobox row. Ticking it selects every name in {@link CategoryRow.rawNames}. */
export interface CategoryRow {
  /** The category key: a built-in category, a custom type's name, or Uncategorised. */
  category: string;
  label: string;
  /** Chip background, the table chips' own {@link categoryColour}. */
  color: string;
  isCustom: boolean;
  /** Full membership, sorted: the category's static raw names plus the raw names on screen. */
  rawNames: string[];
  /**
   * How each of `rawNames` reads ({@link detectorDisplayName}), sorted. Empty on a custom row: its
   * one detector reads as `label` itself, which the chip already shows and search already matches.
   */
  displayNames: string[];
  /** Distinct documents among the rows handed in that carry at least one of `rawNames`. */
  docs: number;
}

export const CUSTOM_TYPES_GROUP = "Custom Types";
export const BUILT_IN_GROUP = "Built-in Categories";
export type CategoryGroupName = typeof CUSTOM_TYPES_GROUP | typeof BUILT_IN_GROUP;

export interface CategoryGroup {
  name: CategoryGroupName;
  categories: CategoryRow[];
}

export interface CategoryList {
  /** Custom Types then Built-in Categories, each pruned; a group with no rows is dropped. */
  groups: CategoryGroup[];
}

/** Built-in category key -> its static raw names, the inverse of the page's `infoTypes` map. */
const STATIC_RAW_NAMES: ReadonlyMap<string, readonly string[]> = (() => {
  const rawNamesByCategory = new Map<string, string[]>();
  for (const rawName of Object.keys(infoTypes)) {
    const category = categoryOf(rawName).category;
    if (category === UNCATEGORISED_CATEGORY) continue;
    const list = rawNamesByCategory.get(category);
    if (list) list.push(rawName);
    else rawNamesByCategory.set(category, [rawName]);
  }
  return rawNamesByCategory;
})();

/** The lookup "which category does this raw name belong to", with the org's custom types first. */
function categoryLookup(customInfoTypes: NamedCustomInfoType[] | undefined): (rawName: string) => string {
  const customTypes = customInfoTypesByName(customInfoTypes);
  return (rawName) => categoryOf(rawName, customTypes).category;
}

const rawNamesOf = (doc: ExposedSensitiveDocument): string[] => Object.keys(doc.sdpInfoTypeCounts ?? {});

const byName = (a: string, b: string) => a.localeCompare(b);

/**
 * Every category's row, **unpruned** - including the zero-document ones - in no particular order.
 *
 * Counting is by *distinct document*: a document carrying two raw names of one category counts
 * once for it.
 */
export function allCategoryRows(docs: ExposedSensitiveDocument[], options: CategoryListOptions = {}): CategoryRow[] {
  return categoryRowsWith(docs, options, categoryLookup(options.customInfoTypes));
}

/**
 * Every raw name any category counts as a member - the union of every row's
 * {@link CategoryRow.rawNames}, without building the rows. The active-category chips count their
 * "k of n types" against this, so a chip is partial exactly when this search's tick is
 * indeterminate (#2249).
 */
export function categoryMembershipOf(docs: ExposedSensitiveDocument[], options: CategoryListOptions = {}): string[] {
  const categoryOfRaw = categoryLookup(options.customInfoTypes);
  const members = membershipWith(docs, options, categoryOfRaw, categoryMetaOf(options));
  return [...members.values()].flatMap((names) => [...names]);
}

const categoryMetaOf = (options: CategoryListOptions) =>
  getCategoryMetaWithCustom((options.customInfoTypes ?? []).map((customType) => ({ ...customType, enabled: true })));

/**
 * Category key -> its full membership: static raw names (not shadowed by a custom type of the
 * same name) plus the raw names on screen and the active ones, so a ticked uncategorised name can
 * still be unticked.
 */
function membershipWith(
  docs: ExposedSensitiveDocument[],
  options: CategoryListOptions,
  categoryOfRaw: (rawName: string) => string,
  meta: ReturnType<typeof categoryMetaOf>,
): Map<string, Set<string>> {
  const rawNamesByCategory = new Map<string, Set<string>>();
  for (const category of [...Object.keys(meta), UNCATEGORISED_CATEGORY]) {
    const statics = meta[category]?.isCustom ? [category] : (STATIC_RAW_NAMES.get(category) ?? []);
    rawNamesByCategory.set(category, new Set(statics.filter((raw) => categoryOfRaw(raw) === category)));
  }
  for (const doc of docs) for (const raw of rawNamesOf(doc)) rawNamesByCategory.get(categoryOfRaw(raw))?.add(raw);
  for (const raw of options.active ?? []) rawNamesByCategory.get(categoryOfRaw(raw))?.add(raw);
  return rawNamesByCategory;
}

function categoryRowsWith(
  docs: ExposedSensitiveDocument[],
  options: CategoryListOptions,
  categoryOfRaw: (rawName: string) => string,
): CategoryRow[] {
  const meta = categoryMetaOf(options);

  // 1. Membership, and the distinct documents behind each category.
  const rawNamesByCategory = membershipWith(docs, options, categoryOfRaw, meta);
  const docsByCategory = new Map<string, Set<string>>();
  for (const doc of docs) {
    for (const raw of rawNamesOf(doc)) {
      const category = categoryOfRaw(raw);
      const docIds = docsByCategory.get(category) ?? new Set<string>();
      docIds.add(doc.resourceId);
      docsByCategory.set(category, docIds);
    }
  }

  // 2. A row per category.
  return [...rawNamesByCategory].map(([category, members]) => {
    const isCustom = meta[category]?.isCustom ?? false;
    const rawNames = [...members].sort(byName);
    const label = category === UNCATEGORISED_CATEGORY ? UNCATEGORISED_LABEL : (meta[category]?.label ?? category);
    return {
      category,
      label,
      color: categoryColour({ category, isCustom }),
      isCustom,
      rawNames,
      displayNames: isCustom ? [] : rawNames.map((raw) => detectorDisplayName(raw, { label })).sort(byName),
      docs: docsByCategory.get(category)?.size ?? 0,
    };
  });
}

/** Build the combobox's category list from the rows of the current exposure + provider view. */
export function buildCategoryList(rows: ExposedSensitiveDocument[], options: CategoryListOptions = {}): CategoryList {
  const categoryOfRaw = categoryLookup(options.customInfoTypes);
  const ticked = new Set((options.active ?? []).map(categoryOfRaw));

  // Prune to what has a document or is ticked, then group and sort (spec 6.3).
  const listed = categoryRowsWith(rows, options, categoryOfRaw)
    .filter((row) => row.docs > 0 || ticked.has(row.category))
    .sort(byLabelUncategorisedLast);
  const groups: CategoryGroup[] = [
    { name: CUSTOM_TYPES_GROUP, categories: listed.filter((row) => row.isCustom) },
    { name: BUILT_IN_GROUP, categories: listed.filter((row) => !row.isCustom) },
  ];
  return { groups: groups.filter((group) => group.categories.length > 0) };
}

/**
 * The groups narrowed to rows whose label or raw names' display names contain `query`,
 * case-insensitively. A blank query is no narrowing; a group left empty is dropped.
 */
export function searchCategories(groups: CategoryGroup[], query: string): CategoryGroup[] {
  const needle = query.trim().toLowerCase();
  if (needle === "") return groups;
  const matches = (row: CategoryRow) =>
    [row.label, ...row.displayNames].some((text) => text.toLowerCase().includes(needle));
  return groups
    .map((group) => ({ ...group, categories: group.categories.filter(matches) }))
    .filter((group) => group.categories.length > 0);
}

export type TriState = "none" | "some" | "all";

/** Checkbox state for one row: unchecked, indeterminate (partial) or checked. Show-all is none. */
export function categoryState(row: CategoryRow, active: string[] | null): TriState {
  if (row.rawNames.length === 0 || active === null) return "none";
  const selected = new Set(active);
  const on = row.rawNames.filter((raw) => selected.has(raw)).length;
  return on === 0 ? "none" : on === row.rawNames.length ? "all" : "some";
}

/**
 * The URL selection after ticking or unticking a row, applied at once (no draft). A checked row
 * is unticked, removing all its names; an unchecked or partial one selects the whole category.
 */
export function toggleCategory(row: CategoryRow, active: string[] | null): ActiveInfoTypeSelection {
  const current = active ?? [];
  if (categoryState(row, active) === "all") {
    const dropped = new Set(row.rawNames);
    return appliedInfoTypes(current.filter((raw) => !dropped.has(raw)));
  }
  const present = new Set(current);
  return appliedInfoTypes([...current, ...row.rawNames.filter((raw) => !present.has(raw))]);
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** The document count at the end of a row: `1 doc`, `4 docs`. */
export function docsLabel(docs: number): string {
  return plural(docs, "doc", "docs");
}

/**
 * The list to save as the org default.
 *
 * The active list is already raw names, so this is nearly the identity - except for
 * Uncategorised, whose membership is data-dependent (spec 8.0 R5): if the list holds any
 * uncategorised name, the selection is re-expanded to *all* the uncategorised names present in
 * `rows` at this moment, so a row that arrived since it was ticked is not silently excluded.
 * De-duplicated, list order first, additions appended alphabetically.
 */
export function expandForSave(
  active: string[],
  rows: ExposedSensitiveDocument[],
  options: CategorySearchOptions = {},
): string[] {
  const categoryOfRaw = categoryLookup(options.customInfoTypes);
  const isUncategorised = (raw: string) => categoryOfRaw(raw) === UNCATEGORISED_CATEGORY;
  const chosen = [...new Set(active)];
  if (!chosen.some(isUncategorised)) return chosen;

  const present = new Set(chosen);
  const additions = [
    ...new Set(rows.flatMap(rawNamesOf).filter((raw) => isUncategorised(raw) && !present.has(raw))),
  ].sort(byName);
  return [...chosen, ...additions];
}
